import React from 'react';
import { Space, Typography, Divider, Button, Popconfirm } from 'antd';
import { DeleteOutlined } from '@ant-design/icons';
import { StrategyPhase } from '../ProfitScenario/types';
import { calculatePhaseMetrics } from '../ProfitScenario/calculations';
import { PhaseFlowDiagram } from './PhaseFlowDiagram';
import { calculateEndDate } from './utils';

const { Text } = Typography;

interface PhaseHeaderProps {
    phase: StrategyPhase;
    landedCost: number;
    fbaFee: number;
    referralRate: number;
    storageFee: number;
    phasesCount: number;
    updatePhaseInfo: (phaseId: string, updates: any) => void;
    deletePhase: (phaseId: string) => void;
}

// 获取阶段主题色（最深）
export const getPhaseTheme = (phaseName: string) => {
    if (phaseName.includes('新品推广') || phaseName.includes('阶段1')) {
        return {
            name: '新品推广期',
            bgColor: '#f9fff6',        // 阶段背景（更浅）
            headerBg: '#ecfce5',       // 阶段标题（更浅）
            borderColor: '#d3f1df',    // 边框（更浅）
            textColor: '#52c41a'       // 文字（最深）
        };
    } else if (phaseName.includes('排名增长') || phaseName.includes('阶段2')) {
        return {
            name: '排名增长期',
            bgColor: '#f5fbff',
            headerBg: '#e6f4ff',
            borderColor: '#d4edff',
            textColor: '#1890ff'
        };
    } else {
        return {
            name: '稳定盈利期',
            bgColor: '#fffef5',
            headerBg: '#fffaeb',
            borderColor: '#fff3d6',
            textColor: '#faad14'
        };
    }
};

export const PhaseHeader: React.FC<PhaseHeaderProps> = ({
    phase,
    landedCost,
    fbaFee,
    referralRate,
    storageFee,
    phasesCount,
    updatePhaseInfo,
    deletePhase
}) => {
    const phaseMetrics = calculatePhaseMetrics(phase, landedCost, fbaFee, referralRate, storageFee);
    const theme = getPhaseTheme(phase.name);

    return (
        <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            width: '100%',
            padding: '12px 16px',
            background: theme.headerBg,
            borderRadius: '6px 6px 0 0',
            borderLeft: `4px solid ${theme.textColor}`,
            borderBottom: `1px solid ${theme.borderColor}`
        }}>
            <Space>
                <Text strong style={{ fontSize: 16, color: theme.textColor }}>
                    {phase.name}
                </Text>
                <Text type="secondary" style={{ fontSize: 13 }}>
                    (W{phase.startWeek}-W{phase.endWeek}, 共{phase.weeks.length}周)
                </Text>
                {phase.startDate && (
                    <Text type="secondary" style={{ fontSize: 13 }}>
                        {phase.startDate} ~ {calculateEndDate(phase.startDate, phase.weeks.length)}
                    </Text>
                )}
            </Space>
            <Space size={16} onClick={e => e.stopPropagation()}>
                <Space size={4} style={{ fontSize: 13, color: '#666' }}>
                    <span>阶段销售: ${phaseMetrics.totalSales.toFixed(0)}</span>
                    <Divider type="vertical" />
                    <span>净利: ${phaseMetrics.totalNetProfit.toFixed(0)} ({phaseMetrics.netMargin.toFixed(1)}%)</span>
                </Space>
                <PhaseFlowDiagram
                    phaseName={phase.name}
                    flowDiagram={phase.flowDiagram}
                    onUpdate={(diagram) => updatePhaseInfo(phase.id, { flowDiagram: diagram })}
                />
                {phasesCount > 1 && (
                    <Popconfirm title="确定删除此阶段?" onConfirm={() => deletePhase(phase.id)}>
                        <Button type="text" danger icon={<DeleteOutlined />} size="small" />
                    </Popconfirm>
                )}
            </Space>
        </div>
    );
};
